<template>
  <tr>
    <td class="align-middle">{{ uniqueWriteIn[0] }}</td>
    <td class="align-middle">{{ uniqueWriteIn[1] }}</td>
    <td class="p-0">
      <v-select
        v-model="selectedOption"
        :options="expandedSelectables"
        :selectable="(option) => !option.code.includes('placeholder')"
        :reduce="(option: ConferenceOption) => option.code"
        :disabled="disableSelect"
        @search="onSearch"
      />
    </td>
  </tr>
</template>

<script setup lang="ts">
  import "vue-select/dist/vue-select.css";
  import { computed, ref, watch, watchEffect } from "vue";
  import { useI18n } from "vue-i18n";
  import type {
    ConferenceOption,
    MappableWriteInOption,
    PropType,
    WriteInMapping,
    UniqueWriteIn
  } from "@/types";

  const { t } = useI18n();

  const props = defineProps({
    allWriteIns: {
      type: Array as PropType<String[]>,
      required: true,
    },
    uniqueWriteIn: {
      type: Object as PropType<UniqueWriteIn>,
      required: true,
    },
    optionReference: {
      type: String,
      required: true,
    }, 
    writeInMapping: {
      type: Object as PropType<WriteInMapping>,
      required: true,
    },
    selectableOptions: {
      type: Array as PropType<MappableWriteInOption[]>,
      required: true,
    },
    disableSelect: {
      type: Boolean,
      default: false,
    },
  });

  const emits = defineEmits(["update-mapping"]);

  const selectedOption = ref("");

  const searchHistory = ref([]);

  const search = ref("");

  const debounceTimeout = ref(null);

  watchEffect(() => (selectedOption.value = props.uniqueWriteIn[0].toString()));

  const expandedSelectables = computed(() => {
    const current = props.uniqueWriteIn[0];
    const newOption = { code: current, label: `${current} (${t('backend.e2e.voting_round_reports.show.original')})` };
    const placeholder = { code: "placeholder", label: t("backend.e2e.voting_round_reports.show.placeholder") };
    return [placeholder, newOption, ...props.selectableOptions, ...searchHistory.value];
  });

  const onSearch = (query: string) => {
    // Debounce this search so we don't instantly add a million options to the list
    clearTimeout(debounceTimeout.value);
    debounceTimeout.value = setTimeout(() => {
      search.value = query;
      if (search.value) {
        props.allWriteIns.forEach(writeIn => {
          if (expandedSelectables.value.some(savedWriteIn => savedWriteIn.code === writeIn)) return; 
          if (writeIn.includes(search.value)) searchHistory.value.push({ code: writeIn, label: writeIn });
          // Can't do filter here as they will dissapear when search is done and UI won't display them anymore
        });
      };
    }, 300);
  };

  watch(selectedOption, () => {
    if (!selectedOption.value) selectedOption.value = props.uniqueWriteIn[0];
    emits("update-mapping", selectedOption.value);
  });
</script>

<style lang="scss">
.vs__dropdown-toggle {
  cursor: pointer;
}

.vs__selected {
  padding: .25rem .125rem;
}

.vs__dropdown-toggle {
  border: none;
  margin: 0;
}

.vs__clear {
  display: flex;
  align-items: center;
}

.vs__dropdown-menu {
  top: calc(100% + 0.5rem);
}
</style>